import { FileText, Shield } from 'lucide-react';

import { t } from '@ping/helpers';
import { ButtonA11Y, Modal } from '@ping/uikit';

import { CampaignLegalMarkdown } from './CampaignLegalMarkdown';
import style from './campaign-legal-document-modal.module.scss';

export type CampaignLegalDocumentType = 'terms' | 'privacy';

interface ICampaignLegalDocumentModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  documentType: CampaignLegalDocumentType | null;
  content?: string | null;
  contentFormat?: 'markdown' | 'plain';
}

const DOCUMENT_META: Record<CampaignLegalDocumentType, { title: string; Icon: typeof FileText }> = {
  terms: { title: 'Terms and conditions', Icon: FileText },
  privacy: { title: 'Privacy policy', Icon: Shield },
};

export const CampaignLegalDocumentModal = ({
  isOpen,
  onOpenChange,
  documentType,
  content,
  contentFormat = 'markdown',
}: ICampaignLegalDocumentModalProps) => {
  if (!documentType) {
    return null;
  }

  const meta = DOCUMENT_META[documentType];
  const Icon = meta.Icon;

  return (
    <Modal.Root isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal.Portal className={style['legalModalPortal']} isDismissable divide entrance='center'>
        <div className={style['legalModalHeader']}>
          <div className={style['legalModalTitleRow']}>
            <span className={style['legalModalIcon']} aria-hidden>
              <Icon size={18} />
            </span>
            <Modal.Header className={style['legalModalHeaderInner']}>
              <Modal.Title className={style['legalModalTitle']}>{t(meta.title)}</Modal.Title>
            </Modal.Header>
          </div>
          <Modal.CloseButton className={style['legalModalClose']} />
        </div>

        <Modal.Content className={style['legalModalContent']}>
          <article
            className={
              contentFormat === 'plain'
                ? `${style['legalModalBody']} ${style['legalModalBodyPlain']}`
                : `${style['legalModalBody']} ${style['legalModalBodyMarkdown']}`
            }
          >
            {contentFormat === 'plain' ? content : <CampaignLegalMarkdown content={content ?? ''} />}
          </article>
        </Modal.Content>

        <Modal.Actions className={style['legalModalActions']}>
          <ButtonA11Y primary className={style['legalModalDone']} onPress={() => onOpenChange(false)}>
            {t('Close')}
          </ButtonA11Y>
        </Modal.Actions>
      </Modal.Portal>
    </Modal.Root>
  );
};
